// BUILD-046 correction cycle 1bb88b3e (stage B2b, items N5 and N6): the task History disclosure - what `GET /work/{id}/history` returns, in words.
//
// The history route returns, oldest first, three kinds of entry: `history` (something happened to the task: who did it and when), `progress` (a NOTE:
// attributed, with the activity date it records and the day it was recorded, and `viaCompletion` when it was written with a Done) and a legacy `note`
// (the task's older single notes field). Every line leads with its DATE (Owner direction 2026-10-22): "10/21/2026 - Created by <name>", "10/22/2026 - Note by <name> (recorded 10/22/2026)" with its text
// (the date of a note is its activity date; the day it was recorded follows in brackets); it never replaces the
// description. A note whose text the viewer may not see (`redacted`) says so politely and shows no text. The server decides who may read the history; this
// module only words what it was given and never invents an actor, a date or a text.
//
// Pure module. Runtime import: the date formatter of taskRowModel (pure) and phoenixToday of dateLabels. Erasable TypeScript only.

import { phoenixToday } from './dateLabels';
import { formatDueDate } from './taskRowModel';
// D1 display cleanup: the actor is written First name + last initial (a role word such as Manager is never a surname) without the "(FICTIONAL)" test marker;
// the headline with the full name is `headlineFull`. A note's text is the writer's own words and is not changed.
import { personDisplay } from './displayNames';

export const HISTORY_EMPTY_TEXT = 'Nothing has been recorded for this task yet.';
export const HISTORY_TRUNCATED_TEXT = 'Only the most recent entries are shown.';
export const NOTE_TEXT_HIDDEN = 'The text of this note is not shown to you.';
export const NOTE_ADDED_WITH_COMPLETION = 'Added when the task was completed';

export interface HistoryRow {
  id: string;
  kind: 'note' | 'event';
  /** The attributed, dated line. The actor is First name + last initial. */
  headline: string;
  /** The same line with the actor's full name (hover and focus text); null when the actor has no longer form or the entry names none. */
  headlineFull: string | null;
  /** The note's text; null for an event, and for a note the viewer may not read. */
  text: string | null;
  /** A short note about a note (for example that it was added with the completion, or that its text is not shown to the viewer). Null for an event. */
  tag: string | null;
  /** What an Edited / Update event changed, one entry per field, in the order the line shows them: Task due date, Next follow-up date, Status, Next action. Empty for a note. */
  details: string[];
  /** The short reason given when an existing date was changed or cleared (without the word "Reason"); null when none was given. */
  reason: string | null;
  redacted: boolean;
}

export type LinePartKind = 'headline' | 'detail' | 'reason' | 'text' | 'tag';

/** One piece of the single line an entry is drawn as. */
export interface LinePart {
  kind: LinePartKind;
  /** Stable within the line. */
  key: string;
  /** The words of the piece (for the reason: just the reason; its label is `label`). */
  text: string;
  /** A lead-in drawn lighter before the text ("Reason:"), or null. */
  label: string | null;
  /** The full wording, for a piece that is cut off with an ellipsis when the line is too long for the window. */
  title: string;
  /** The headline only: the same line with the actor's full name (hover and focus text), or null. */
  full: string | null;
}

export interface HistoryView {
  rows: HistoryRow[];
  emptyText: string | null;
  truncatedText: string | null;
}

const ACTION_WORDS: Readonly<Record<string, string>> = Object.freeze({
  created: 'Created',
  updated: 'Edited',
  completed: 'Completed',
  restored: 'Reopened',
  claimed: 'Taken',
  reassigned: 'Reassigned',
  returned: 'Returned to the queue',
  cancelled: 'Cancelled',
  recipient_added: 'Recipient added',
  recipient_removed: 'Recipient removed',
  handoff: 'Handed off',
  handed_off: 'Handed off',
});

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The actor as written in the entry (kept as recorded); 'someone' when the entry names none. */
function actorName(entry: Record<string, unknown>): string {
  const actor = entry.actor;
  if (actor !== null && typeof actor === 'object') {
    const name = textOf((actor as { name?: unknown }).name);
    if (name !== '') return name;
  }
  return 'someone';
}

/** The actor in the two forms a line can carry: the short form it shows, and the full name its hover and focus text gives (null when they are the same). */
function actorForms(entry: Record<string, unknown>): { short: string; full: string | null } {
  const recorded = actorName(entry);
  const person = personDisplay(recorded);
  const short = person.short !== '' ? person.short : recorded;
  return { short, full: person.full !== '' && person.full !== short ? person.full : null };
}

/** `MM/DD/YYYY` from a `YYYY-MM-DD` value, or from the Phoenix calendar day of an ISO instant; '' when neither can be read. */
export function historyDate(value: unknown): string {
  const direct = formatDueDate(value);
  if (direct !== null) return direct;
  if (typeof value === 'string' && value !== '') {
    const ms = Date.parse(value);
    if (Number.isFinite(ms)) return formatDueDate(phoenixToday(new Date(ms))) ?? '';
  }
  return '';
}

// BUILD-046 section C (Owner direction of 2026-10-21): an Edited / Update entry names what changed in the fields that matter for follow-up - the status, the next follow-up date, the Task due date and
// the next action - with the old and the new value, and the short reason given when an existing date was changed or cleared. The server sends `changes` ({ from?, to? } per field) and `reason`; a
// viewer who may not read the task gets neither (the server withholds them). Other fields an edit touched (title, description, notes, people) are not listed here.
// The ORDER is the order of the line (Owner direction 2026-10-22: "Edited by" first, then the Task due date, then the reason, all on one line): Task due date, Next follow-up date, Status, Next action.
const CHANGE_FIELDS: readonly { key: string; label: string; kind: "status" | "date" | "text" }[] = Object.freeze([
  { key: 'dueDate', label: 'Task due date', kind: 'date' },
  { key: 'followUpDate', label: 'Next follow-up date', kind: 'date' },
  { key: 'status', label: 'Status', kind: 'status' },
  { key: 'nextAction', label: 'Next action', kind: 'text' },
  // 2026-10-24 Owner supplement: the requester's changes to the assignment are recorded by name (the server writes `changes.job` and `changes.collaborators` as text), so the line reads them back.
  // CHANGED 2026-10-25: the DISPLAY label is "Job" (was "Customer / Job"); the key stays 'job' (it is the stored change name). The customer's own change is the 'customer' line beside it.
  { key: 'job', label: 'Job', kind: 'text' },
  // 2026-10-24 Owner supplement (BUILD-046 Customer association): a change of the task's Customer is recorded by name under its own key (`changes.customer`), beside the Job, which is untouched.
  { key: 'customer', label: 'Customer', kind: 'text' },
  { key: 'collaborators', label: 'Collaborators', kind: 'text' },
]);
const STATUS_WORDS: Readonly<Record<string, string>> = Object.freeze({ active: 'Active', in_progress: 'In progress', waiting: 'Waiting', blocked: 'Blocked', completed: 'Completed' });

function changeValue(kind: 'status' | 'date' | 'text', value: unknown): string {
  const raw = textOf(value);
  if (raw === '') return '';
  if (kind === 'status') return STATUS_WORDS[raw] ?? raw;
  if (kind === 'date') return historyDate(raw) || raw;
  return raw;
}

/** One line per changed field of interest: "Task due date: 10/30/2026 to 11/02/2026", "Next follow-up date: cleared (was 10/25/2026)", "Next action: set to Call back". */
export function changeLines(changes: unknown): string[] {
  if (changes === null || typeof changes !== 'object' || Array.isArray(changes)) return [];
  const record = changes as Record<string, unknown>;
  const lines: string[] = [];
  for (const field of CHANGE_FIELDS) {
    const change = record[field.key];
    if (change === null || typeof change !== 'object') continue;
    const from = changeValue(field.kind, (change as { from?: unknown }).from);
    const to = changeValue(field.kind, (change as { to?: unknown }).to);
    if (from === '' && to === '') continue;
    lines.push(from === '' ? `${field.label}: set to ${to}` : to === '' ? `${field.label}: cleared (was ${from})` : `${field.label}: ${from} to ${to}`);
  }
  // BUILD-046 integration: a hand off completes the original and creates another task for someone else; the original's Completed entry names who the work went to, so the person who issued it is not left to guess.
  const handedOffTo = record.handedOffTo;
  if (handedOffTo !== null && typeof handedOffTo === 'object') {
    const to = textOf((handedOffTo as { to?: unknown }).to);
    if (to !== '') lines.unshift(`Handed off to ${to}`);
  }
  return lines;
}

/** What is said where an ownership event names a person the server could not name (a viewer who may not see the person, an id that cannot be resolved). Nothing is inferred from the current assignee. */
export const OTHER_PERSON_WORDS = "another person";

/**
 * BUILD-046 G4 (Builder decision 2026-10-22, October 23 addendum, item 4): who an ownership event moved the task FROM and TO, from the ids the event recorded (`previousOwner`, `newOwner`) and the names the server resolved for a viewer who may read the
 * task (`previousOwnerName`, `newOwnerName`). The event itself is never rewritten and nothing is read from the current assignee. A reassignment reads "From Eddie F. to Ben M." beside "Reassigned by Daris D."; when only one side can be named the other
 * is "another person"; when neither can be named (a restricted viewer, an unresolved id) the line is left out and the headline stands.
 */
export function ownershipLines(entry: Record<string, unknown>): string[] {
  const action = textOf(entry.action);
  const has = (value: unknown): boolean => typeof value === "string" && value.trim() !== "";
  const hasPrevious = has(entry.previousOwner);
  const hasNew = has(entry.newOwner);
  const previous = textOf(entry.previousOwnerName);
  const next = textOf(entry.newOwnerName);
  if (previous === "" && next === "") return [];
  const show = (name: string): string => (name === "" ? OTHER_PERSON_WORDS : personDisplay(name).short || name);
  switch (action) {
    case "reassigned":
      if (hasPrevious && hasNew) return ["From " + show(previous) + " to " + show(next)];
      return hasNew ? ["To " + show(next)] : [];
    case "returned":
      return hasPrevious ? ["Was with " + show(previous)] : [];
    case "recipient_added":
      return hasNew ? ["Added: " + show(next)] : [];
    case "recipient_removed":
      return hasPrevious ? ["Removed: " + show(previous)] : [];
    default:
      return [];
  }
}

/** "10/21/2026 - " when the entry has a readable date, else nothing (no date is made up). */
function dated(date: string): string {
  return date !== '' ? `${date} - ` : '';
}

function humanize(action: string): string {
  const words = action.replace(/[_-]+/g, ' ').trim();
  return words === '' ? 'Changed' : `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

function rowOf(entry: Record<string, unknown>, index: number): HistoryRow | null {
  const type = textOf(entry.type);
  const id = textOf(entry.id) || `entry-${index}`;
  if (type === 'progress') {
    const activity = historyDate(entry.activityDate);
    const recorded = historyDate(entry.recordedPhoenixDate) || historyDate(entry.timestamp);
    const redacted = entry.redacted === true;
    const text = redacted ? null : textOf(entry.text) === '' ? null : textOf(entry.text);
    // The line leads with the activity date (the day the work happened); when the note has none, with the day it was recorded. The recorded day follows in brackets when both are known.
    const lead = activity !== '' ? activity : recorded;
    const dates = activity !== '' && recorded !== '' ? `(recorded ${recorded})` : '';
    const who = actorForms(entry);
    return {
      id,
      kind: 'note',
      headline: `${dated(lead)}Note by ${who.short}${dates !== '' ? ` ${dates}` : ''}`,
      headlineFull: who.full === null ? null : `${dated(lead)}Note by ${who.full}${dates !== '' ? ` ${dates}` : ''}`,
      text,
      tag: redacted ? NOTE_TEXT_HIDDEN : entry.viaCompletion === true ? NOTE_ADDED_WITH_COMPLETION : null,
      details: [],
      reason: null,
      redacted,
    };
  }
  if (type === 'note') {
    const created = historyDate(entry.timestamp);
    return { id, kind: 'note', headline: `${dated(created)}Earlier note`, headlineFull: null, text: textOf(entry.text) === '' ? null : textOf(entry.text), tag: null, details: [], reason: null, redacted: false };
  }
  if (type === 'history') {
    const action = textOf(entry.action);
    const when = historyDate(entry.timestamp);
    const word = ACTION_WORDS[action] ?? humanize(action);
    const who = actorForms(entry);
    const lines = [...ownershipLines(entry), ...changeLines(entry.changes)];
    const reason = textOf(entry.reason);
    // Owner decision D8: an edit made by the person who ISSUED the task (not the one doing it) says so - "Edited by Chris (requester)" - so the assignee can tell the two apart.
    const capacity = entry.capacity === 'requester' ? ' (requester)' : '';
    return {
      id,
      kind: 'event',
      headline: `${dated(when)}${word} by ${who.short}${capacity}`,
      headlineFull: who.full === null ? null : `${dated(when)}${word} by ${who.full}${capacity}`,
      text: null,
      tag: null,
      details: lines,
      reason: reason !== '' ? reason : null,
      redacted: false,
    };
  }
  return null;
}

/**
 * The pieces of the ONE line an entry is drawn as, left to right (Owner direction 2026-10-22: one line per entry, the space to the right used, "Edited by" first, then the Task due date, then the reason):
 * the headline ("10/21/2026 - Edited by Alice A."), then what the event changed (Task due date first), then the reason, then a note's text, then a note's tag. Nothing is made up: a piece exists only when
 * the entry has it.
 */
export function linePartsOf(row: HistoryRow): LinePart[] {
  const parts: LinePart[] = [{ kind: 'headline', key: 'headline', text: row.headline, label: null, title: row.headlineFull ?? row.headline, full: row.headlineFull }];
  row.details.forEach((detail, index) => parts.push({ kind: 'detail', key: `detail-${index}`, text: detail, label: null, title: detail, full: null }));
  if (row.reason !== null) parts.push({ kind: 'reason', key: 'reason', text: row.reason, label: 'Reason:', title: `Reason: ${row.reason}`, full: null });
  if (row.text !== null) parts.push({ kind: 'text', key: 'text', text: row.text, label: null, title: row.text, full: null });
  if (row.tag !== null) parts.push({ kind: 'tag', key: 'tag', text: row.tag, label: null, title: row.tag, full: null });
  return parts;
}

/** The entries as rows, oldest first as the server sent them. An entry of an unknown kind is left out. */
export function describeHistory(history: readonly Record<string, unknown>[] | null | undefined, truncated: boolean = false): HistoryView {
  const rows: HistoryRow[] = [];
  (Array.isArray(history) ? history : []).forEach((entry, index) => {
    if (entry === null || typeof entry !== 'object') return;
    const row = rowOf(entry, index);
    if (row !== null) rows.push(row);
  });
  return { rows, emptyText: rows.length === 0 ? HISTORY_EMPTY_TEXT : null, truncatedText: truncated ? HISTORY_TRUNCATED_TEXT : null };
}

export type HistoryLoad = { phase: 'loading' } | { phase: 'ready'; view: HistoryView } | { phase: 'error'; message: string };

export function historyFailed(thrown: unknown): HistoryLoad {
  const message = thrown !== null && typeof thrown === 'object' && typeof (thrown as { message?: unknown }).message === 'string' ? (thrown as { message: string }).message.trim() : '';
  return { phase: 'error', message: message === '' ? 'The history could not be loaded.' : `The history could not be loaded: ${message}` };
}
